/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Cml_SubtitleTrack as SubtitleTrack } from '__generated__/graphql-types-do-not-use';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import { AssetTypeNames } from 'bundles/asset-admin/types/assets';
import NonMediaAsset from 'bundles/cml/legacy/components/NonMediaAsset';
import type { VideoAssetProps } from 'bundles/cml/legacy/components/VideoAsset';
import type { VideoResolutions } from 'bundles/cml/legacy/types/assets';
import { isPDfEmbedEnabled, isSupportedAudioAsset, isSupportedVideoAsset } from 'bundles/cml/legacy/utils/AssetUtils';
import { EmbeddablePdfAsset } from 'bundles/cml/shared/components/asset/EmbeddablePdfAsset';
import type { AssetData } from 'bundles/cml/shared/types/assetDataTypes';
import type { AssetElement } from 'bundles/cml/shared/types/elementTypes';

const VideoAsset = createLoadableComponent(() => import('bundles/cml/legacy/components/VideoAsset'));
const AudioAsset = createLoadableComponent(() => import('bundles/cml/legacy/components/AudioAsset'));

const styles = {
  root: css`
    position: relative;
    outline: none;
    user-select: none;
  `,
};

export type AssetProps = {
  id: string;
  assetData: AssetData;
  assetElement?: AssetElement;
  onOpenMenu?: () => void;
  videoResolutions?: VideoResolutions;
  subtitleTracks?: SubtitleTrack[];
};

export const isAssetPropsVideo = (assetProps: AssetProps): assetProps is VideoAssetProps => {
  return assetProps.assetData?.type === AssetTypeNames.VIDEO;
};

const AssetV2 = (props: Omit<AssetProps, 'onOpenMenu'>) => {
  const { id, assetElement, assetData } = props;

  // key is necessary to re-mount the component once asset data is loaded
  const nodeKey = `asset-node-${id}`;

  if (isSupportedAudioAsset(assetData)) {
    return <AudioAsset key={nodeKey} id={id} assetData={assetData} />;
  }

  if (isAssetPropsVideo(props) && isSupportedVideoAsset(assetData)) {
    return (
      <VideoAsset
        key={nodeKey}
        id={id}
        assetData={assetData}
        subtitleTracks={props.subtitleTracks}
        videoResolutions={props.videoResolutions}
      />
    );
  }

  if (assetElement && isPDfEmbedEnabled(assetElement)) {
    return <EmbeddablePdfAsset assetData={assetData} assetElement={assetElement} />;
  }

  return <NonMediaAsset key={nodeKey} id={id} assetData={assetData} />;
};

const AssetProvider = (props: AssetProps) => {
  return (
    <div data-testid="asset-v2-wrapper" css={styles.root} onClickCapture={props.onOpenMenu}>
      <AssetV2 {...props} />
    </div>
  );
};

export default AssetProvider;
